import { useQueryClient } from '@tanstack/react-query'
import { ManagerApprovalRulesResponse } from 'src/models/organizations.models'
import { useHasSaasFeatures } from 'src/queries/organizations/saas.queries'
import { orgQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  useExtendMutation,
  UseExtendMutationVariablesBody,
  useExtendQuery,
} from 'src/queries/queries.utils'
import { useQueryOwnUser } from 'src/queries/users.queries'

interface QueryOrgManagerApprovalRulesOptions
  extends ExtendBaseQueryOptions<ManagerApprovalRulesResponse> {
  orgId: string | undefined
}

export const useQueryManagerReviewRules = ({
  orgId,
  enabled = true,
  ...rest
}: QueryOrgManagerApprovalRulesOptions) => {
  return useExtendQuery({
    queryKey: orgQueryKeys.managerApprovalRules(orgId),
    queryUrl: () => `/expensereview/expensemanagementsettings`,
    enabled: enabled && orgId !== undefined,
    ...rest,
  })
}

export const useManagerReviewRulesEnabled = (): boolean => {
  const { data: user } = useQueryOwnUser({})

  const hasSaasFeatures = useHasSaasFeatures()

  const { data: managerReviewRules } = useQueryManagerReviewRules({
    orgId: user?.organizationId,
    // Conditionally enable this query for only Pro/Enterprise tiers or Saas-excluded orgs,
    // otherwise this will show the SaaS upgrade modal on every render
    enabled: hasSaasFeatures,
  })

  return !!managerReviewRules?.managerReviewEnabled
}

export const usePostManagerReviewRules = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    ManagerApprovalRulesResponse,
    UseExtendMutationVariablesBody<Partial<ManagerApprovalRulesResponse>>
  >(MutationMethod.PATCH, {
    mutationUrl: () => `/expensereview/expensemanagementsettings`,
    onSuccess: (data) => {
      queryClient.setQueryData<ManagerApprovalRulesResponse>(
        orgQueryKeys.managerApprovalRules(data.organizationId),
        data,
      )
    },
  })
}
